import NameLink from '../../components/NameLink/NameLink';
import { MatchTooltip } from '../../components/ScoreboardWC26/ScoreTooltip';
import {
  BracketFixture, BracketStanding, MatchStats, Scores, ScoringRules, UserTeams,
} from '../../types';
import { calculateResults, calculateScores, getMostRedsWinnerIds } from '../../utils/scoringUtils';
import { KNOCKOUT_ROUNDS, SlotDef } from './knockoutData';

interface ResolvedSlot {
  name: string;
  logo?: string;
  player?: string;
  tbd: boolean;
  teamId?: number;
}

const ZERO_TEAM_RESULTS = {
  wins: 0, draws: 0, redCards: 0, cleanSheets: 0, ownGoals: 0, teamGoals3plus: 0,
};
const DUMMY_TEAM_ID = '__none__';

function findPlayer(teamId: number, userTeams: UserTeams[]): string {
  const id = teamId.toString();
  return userTeams.find((u) => u.team1Id === id || u.team2Id === id)?.name ?? '';
}

function resolveSlot(
  slot: SlotDef,
  standings: BracketStanding[],
  qualifying3rdIds: Set<number>,
  matchWinners: Record<number, ResolvedSlot>,
  userTeams: UserTeams[]
): ResolvedSlot {
  if (slot.kind === 'group_winner') {
    const team = standings.find((s) => s.group === slot.group && s.rank === 1);
    return team
      ? {
        name: team.team.name,
        logo: team.team.logo,
        player: findPlayer(team.team.id, userTeams),
        tbd: false,
        teamId: team.team.id,
      }
      : { name: `${slot.group} Winner`, tbd: true };
  }

  if (slot.kind === 'group_runner_up') {
    const team = standings.find((s) => s.group === slot.group && s.rank === 2);
    return team
      ? {
        name: team.team.name,
        logo: team.team.logo,
        player: findPlayer(team.team.id, userTeams),
        tbd: false,
        teamId: team.team.id,
      }
      : { name: `${slot.group} Runner-up`, tbd: true };
  }

  if (slot.kind === 'best_3rd') {
    const eligible = standings.filter(
      (s) => s.rank === 3 && qualifying3rdIds.has(s.team.id)
        && slot.groups.includes(s.group.replace('Group ', ''))
    );
    if (eligible.length === 1) {
      return {
        name: eligible[0].team.name,
        logo: eligible[0].team.logo,
        player: findPlayer(eligible[0].team.id, userTeams),
        tbd: false,
        teamId: eligible[0].team.id,
      };
    }
    return { name: `Best 3rd (${slot.groups.join('/')})`, tbd: true };
  }

  if (slot.kind === 'match_winner' || slot.kind === 'match_runner_up') {
    const key = slot.kind === 'match_winner' ? slot.matchId : -slot.matchId;
    const resolved = matchWinners[key];
    if (resolved) return resolved;
    const label = slot.kind === 'match_winner' ? `Winner M${slot.matchId}` : `Runner-up M${slot.matchId}`;
    return { name: label, tbd: true };
  }

  return { name: 'TBD', tbd: true };
}

function slotFromFixtureSide(
  fixture: BracketFixture,
  side: 'home' | 'away',
  userTeams: UserTeams[]
): ResolvedSlot {
  const team = fixture[side];
  return {
    name: team.name,
    logo: team.logo,
    player: findPlayer(team.id, userTeams) || undefined,
    tbd: false,
    teamId: team.id,
  };
}

interface SlotDisplayProps {
  slot: ResolvedSlot;
  align: 'left' | 'right';
  points: number | null;
  score: Scores | null;
}

function SlotDisplay({
  slot, align, points, score,
}: SlotDisplayProps) {
  return (
    <div className={`knockout-slot ${align} ${slot.tbd ? 'tbd' : ''}`}>
      {slot.logo && <img src={slot.logo} alt={slot.name} />}
      <div className="knockout-slot-info">
        <span>{slot.name}</span>
        {slot.player && (
          <span className="knockout-slot-player">
            <NameLink name={slot.player} />
            {points !== null && score && (
              <span className={`bracket-fixture-points ${points > 0 ? 'positive' : points < 0 ? 'negative' : 'zero'}`}>
                <MatchTooltip
                  score={score}
                  label={points >= 0 ? `+${points}` : `${points}`}
                />
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

interface KnockoutBracketProps {
  standings: BracketStanding[];
  qualifying3rdIds: Set<number>;
  userTeams: UserTeams[];
  bracketFixtures: BracketFixture[];
  matchStatsById: Record<number, MatchStats>;
  scoringRules?: ScoringRules;
  directFkGoals: string[];
  olimpicoGoals: string[];
  keeperGoals: string[];
}

function findFixture(
  homeId: number | undefined,
  awayId: number | undefined,
  fixtures: BracketFixture[]
): BracketFixture | undefined {
  // Fixture IDs in the data source don't correspond to the hardcoded knockout match
  // numbers, so matches must be looked up by the two teams playing (unambiguous once
  // both sides are known, since two teams only meet once in the knockout stage).
  if (!homeId && !awayId) return undefined;
  return fixtures.find((f) => {
    const ids = [f.home.id, f.away.id];
    if (homeId && awayId) return ids.includes(homeId) && ids.includes(awayId);
    return ids.includes(homeId!) || ids.includes(awayId!);
  });
}

function KnockoutBracket({
  standings, qualifying3rdIds, userTeams, bracketFixtures,
  matchStatsById, scoringRules, directFkGoals, olimpicoGoals, keeperGoals,
}: KnockoutBracketProps) {
  const knockoutFixtures = bracketFixtures.filter((f) => !f.round.startsWith('Group Stage'));
  const matchWinners: Record<number, ResolvedSlot> = {};

  const allMatchStatsList = Object.values(matchStatsById);
  const allTeamResults = calculateResults(userTeams, allMatchStatsList);
  const mostRedsWinnerIds = getMostRedsWinnerIds(allTeamResults, userTeams);

  const findScore = (fixtureId: number, teamId: number): Scores | null => {
    const teamIdStr = teamId.toString();
    const userTeam = userTeams.find((u) => u.team1Id === teamIdStr || u.team2Id === teamIdStr);
    const matchStat = matchStatsById[fixtureId];
    if (!userTeam || !matchStat || !scoringRules) return null;
    const teamResults = calculateResults([userTeam], [matchStat]);
    const teamResultsWithDummy = { ...teamResults, [DUMMY_TEAM_ID]: { ...ZERO_TEAM_RESULTS } };
    const isT1 = teamIdStr === userTeam.team1Id;
    const soloUser = {
      ...userTeam,
      team1Id: isT1 ? teamIdStr : DUMMY_TEAM_ID,
      team2Id: isT1 ? DUMMY_TEAM_ID : teamIdStr,
    };
    const [s] = calculateScores(
      teamResultsWithDummy,
      [soloUser],
      scoringRules,
      undefined,
      undefined,
      undefined,
      undefined,
      [matchStat],
      directFkGoals,
      olimpicoGoals,
      keeperGoals,
      mostRedsWinnerIds
    );
    return s;
  };

  const totalFor = (s: Scores) => s.winsScore + s.drawsScore + s.cleanSheetsScore
    + s.redCardsScore + s.ownGoalsScore + s.hattrick + s.directFk
    + s.olimpico + s.keeperGoal + s.mostReds + s.teamGoals3plus;

  return (
    <div className="knockout-bracket">
      {KNOCKOUT_ROUNDS.map((round) => {
        const cols = Math.min(round.matches.length, 4);
        return (
          <div key={round.name} className="knockout-round">
            <div className="knockout-round-header">{round.name}</div>
            <div className="knockout-matches" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
              {round.matches.map((match) => {
                const resolvedHome = resolveSlot(
                  match.home,
                  standings,
                  qualifying3rdIds,
                  matchWinners,
                  userTeams
                );
                const resolvedAway = resolveSlot(
                  match.away,
                  standings,
                  qualifying3rdIds,
                  matchWinners,
                  userTeams
                );
                const fixture = findFixture(
                  resolvedHome.teamId,
                  resolvedAway.teamId,
                  knockoutFixtures
                );
                const home = fixture ? slotFromFixtureSide(fixture, 'home', userTeams) : resolvedHome;
                const away = fixture ? slotFromFixtureSide(fixture, 'away', userTeams) : resolvedAway;

                // Populate matchWinners so later rounds resolve correctly
                if (fixture) {
                  const statusShort = fixture.status.short;
                  const isFinished = ['FT', 'AET', 'PEN'].includes(statusShort);
                  if (isFinished) {
                    let winnerSide: 'home' | 'away' | null = null;
                    const matchStat = matchStatsById[fixture.id];
                    if (statusShort === 'PEN' && matchStat) {
                      // homeWinner/awayWinner from match stats reflects the penalty shootout result
                      if (matchStat.homeWinner) winnerSide = 'home';
                      else if (matchStat.awayWinner) winnerSide = 'away';
                    } else if (fixture.penalty) {
                      const hp = fixture.penalty.home ?? 0;
                      const ap = fixture.penalty.away ?? 0;
                      if (hp !== ap) winnerSide = hp > ap ? 'home' : 'away';
                    } else {
                      const hg = fixture.goals.home ?? -1;
                      const ag = fixture.goals.away ?? -1;
                      if (hg !== ag) winnerSide = hg > ag ? 'home' : 'away';
                    }
                    if (winnerSide) {
                      const loserSide = winnerSide === 'home' ? 'away' : 'home';
                      matchWinners[match.id] = slotFromFixtureSide(fixture, winnerSide, userTeams);
                      matchWinners[-match.id] = slotFromFixtureSide(fixture, loserSide, userTeams);
                    }
                  }
                }

                const statusShort = fixture?.status.short ?? '';
                const isFinishedMatch = ['FT', 'AET', 'PEN'].includes(statusShort);
                const isLive = ['1H', '2H', 'ET', 'HT', 'LIVE'].includes(statusShort);
                const homeGoals = fixture?.goals.home;
                const awayGoals = fixture?.goals.away;
                const penHome = fixture?.penalty?.home;
                const penAway = fixture?.penalty?.away;
                const showScore = isFinishedMatch || isLive;

                const homeScore = (isFinishedMatch || isLive) && home.teamId
                  ? findScore(fixture!.id, home.teamId) : null;
                const awayScore = (isFinishedMatch || isLive) && away.teamId
                  ? findScore(fixture!.id, away.teamId) : null;
                const homePoints = homeScore ? totalFor(homeScore) : null;
                const awayPoints = awayScore ? totalFor(awayScore) : null;

                return (
                  <div key={match.id} className="knockout-match">
                    <div className="knockout-match-meta">
                      <span className="knockout-match-id">{`M${match.id}`}</span>
                      <span className="knockout-match-date">{match.date}</span>
                      <span className="knockout-match-venue">{match.venue}</span>
                    </div>
                    <div className="knockout-match-teams">
                      <SlotDisplay slot={home} align="left" points={homePoints} score={homeScore} />
                      {showScore ? (
                        <div className="knockout-score">
                          <span className="knockout-score-line">
                            {`${homeGoals ?? '-'} – ${awayGoals ?? '-'}`}
                          </span>
                          {statusShort === 'PEN' && penHome != null && penAway != null && (
                            <span className="knockout-score-pens">
                              {`(${penHome} – ${penAway}) pens`}
                            </span>
                          )}
                          <span className={`knockout-score-status ${isLive ? 'live' : 'ft'}`}>
                            {isLive && fixture?.status.elapsed
                              ? `${fixture.status.elapsed}'`
                              : statusShort}
                          </span>
                        </div>
                      ) : (
                        <span className="knockout-vs">vs</span>
                      )}
                      <SlotDisplay slot={away} align="right" points={awayPoints} score={awayScore} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default KnockoutBracket;
